import React, { Children } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { Col, Input, Row } from "reactstrap";

/**
 * Checkbox / radio del sistema. Estilos globales en scss/dhn/_selectable-option.scss.
 *
 * <SelectableOption
 *   icon="fa-shield"
 *   title="Perfil administrador"
 *   description="Habilita capacidades administrativas del sistema."
 *   checked={item.isAdmin}
 *   onChange={({ target }) => onSetItem("isAdmin", target.checked)}
 * />
 *
 * Sin estado propio en el formulario: usar `defaultChecked` en lugar de `checked`.
 * `tooltip` muestra un texto al pasar el cursor (p. ej. motivo de deshabilitado).
 */
export default function SelectableOption({
  checked,
  className,
  compact,
  defaultChecked,
  description,
  disabled,
  icon,
  id,
  inputName,
  inputType,
  invalid,
  onChange,
  title,
  tooltip,
  value,
}) {
  // Con `defaultChecked` (y sin `checked`) el input nativo guarda el estado y el
  // estilo seleccionado se resuelve en CSS con :has(input:checked).
  const isControlled = checked !== undefined || defaultChecked === undefined;
  const checkedProps = isControlled
    ? { checked: Boolean(checked) }
    : { defaultChecked: Boolean(defaultChecked) };

  return (
    <label
      htmlFor={id}
      title={tooltip}
      className={classnames("dhn-selectable-option", className, {
        "is-selected": isControlled && Boolean(checked),
        "is-compact": compact,
        "is-disabled": disabled,
        "is-invalid": invalid,
      })}
    >
      <Input
        id={id}
        type={inputType}
        name={inputName}
        value={value}
        {...checkedProps}
        disabled={disabled}
        onChange={onChange}
      />
      <span className="dhn-selectable-option__icon">
        <i className={classnames("fa", icon)} aria-hidden="true"></i>
      </span>
      <span className="dhn-selectable-option__copy">
        <strong>{title}</strong>
        {description && <small>{description}</small>}
      </span>
    </label>
  );
}

SelectableOption.propTypes = {
  checked: PropTypes.bool,
  className: PropTypes.string,
  compact: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  description: PropTypes.node,
  disabled: PropTypes.bool,
  icon: PropTypes.string.isRequired,
  id: PropTypes.string,
  inputName: PropTypes.string,
  inputType: PropTypes.oneOf(["checkbox", "radio"]),
  invalid: PropTypes.bool,
  onChange: PropTypes.func,
  title: PropTypes.node.isRequired,
  tooltip: PropTypes.string,
  value: PropTypes.any,
};

SelectableOption.defaultProps = {
  compact: false,
  disabled: false,
  inputType: "checkbox",
  invalid: false,
};

/**
 * Check con un campo integrado: el campo (children) solo se habilita cuando la
 * opción está marcada. Para valores que dependen de activar una opción, como
 * un porcentaje o un monto. Con `locksField` funciona al revés: marcar la
 * opción bloquea el campo (p. ej. "verificado"). Un react-select dentro debe
 * recibir también `isDisabled`, porque el fieldset solo deshabilita campos
 * nativos. Estilos globales en scss/dhn/_selectable-option.scss.
 *
 * <SelectableInputOption
 *   icon="fa-percent"
 *   title="ITBIS"
 *   checked={item.hasITBIS}
 *   onChange={({ target }) => onSetItem("hasITBIS", target.checked)}
 * >
 *   <NumberFormatInput customInput={Input} suffix="%" ... />
 * </SelectableInputOption>
 */
export function SelectableInputOption({
  checked,
  children,
  className,
  description,
  disabled,
  icon,
  id,
  inputName,
  invalid,
  locksField,
  onChange,
  title,
  tooltip,
}) {
  const isFieldDisabled = disabled || (locksField ? Boolean(checked) : !checked);

  return (
    <div
      title={tooltip}
      className={classnames("dhn-selectable-input-option", className, {
        "is-selected": Boolean(checked),
        "is-disabled": disabled,
        "is-invalid": invalid,
      })}
    >
      <label htmlFor={id} className="dhn-selectable-input-option__toggle">
        <Input
          id={id}
          type="checkbox"
          name={inputName}
          checked={Boolean(checked)}
          disabled={disabled}
          onChange={onChange}
        />
        <span className="dhn-selectable-option__icon">
          <i className={classnames("fa", icon)} aria-hidden="true"></i>
        </span>
        <span className="dhn-selectable-option__copy">
          <strong>{title}</strong>
          {description && <small>{description}</small>}
        </span>
      </label>
      <fieldset
        className="dhn-selectable-input-option__field"
        disabled={isFieldDisabled}
      >
        {children}
      </fieldset>
    </div>
  );
}

SelectableInputOption.propTypes = {
  checked: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  description: PropTypes.node,
  disabled: PropTypes.bool,
  icon: PropTypes.string.isRequired,
  id: PropTypes.string,
  inputName: PropTypes.string,
  invalid: PropTypes.bool,
  locksField: PropTypes.bool,
  onChange: PropTypes.func,
  title: PropTypes.node.isRequired,
  tooltip: PropTypes.string,
};

SelectableInputOption.defaultProps = {
  disabled: false,
  invalid: false,
  locksField: false,
};

/**
 * Distribuye varias SelectableOption consecutivas y permite que la fila se
 * ajuste automáticamente al espacio disponible.
 */
export function SelectableOptionGroup({
  children,
  className,
  columns,
  fitContent,
  ...props
}) {
  return (
    <Row
      {...props}
      form
      data-columns={columns}
      data-fit-content={fitContent || undefined}
      className={classnames("dhn-selectable-option-group", className)}
    >
      {Children.toArray(children)
        .filter(Boolean)
        .map((child) => (
          <Col key={child.key}>
            {child}
          </Col>
        ))}
    </Row>
  );
}

SelectableOptionGroup.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  // Se conserva para no romper llamados existentes; el grupo ahora se adapta
  // al contenido independientemente de una cantidad fija de columnas.
  columns: PropTypes.oneOf([1, 2, 3, 4, 6]),
  fitContent: PropTypes.bool,
};

SelectableOptionGroup.defaultProps = {
  columns: undefined,
  fitContent: false,
};
